/**
 * OSRM provider — veřejné servery routing.openstreetmap.de (bez API klíče),
 * nebo vlastní endpoint. Data © OpenStreetMap contributors.
 *
 * Trasa: skutečná silniční/stezková síť OSM. Délka v metrech, doba jízdy
 * v sekundách dle profilu (driving/cycling/walking), geometrie polyline.
 */
import { decodePolyline } from '../../core/geo';
import type { LatLng, RoutingProfile } from '../../core/types';
import type { ProviderConfig, ProviderRoute, RoutingProvider } from './types';

const PROFILE_PATH: Record<RoutingProfile, string> = {
  car: 'routed-car',
  bike: 'routed-bike',
  foot: 'routed-foot',
};

const OSRM_MODE: Record<RoutingProfile, string> = {
  car: 'driving',
  bike: 'cycling',
  foot: 'walking',
};

export const osrmProvider: RoutingProvider = {
  id: 'osrm',
  name: 'OSRM',
  description:
    'Veřejné OSRM servery nad daty OpenStreetMap. Bez klíče, vhodné pro běžné použití.',
  requiresKey: false,
  isConfigured: () => true,

  async route(
    profile: RoutingProfile,
    from: LatLng,
    to: LatLng,
    cfg: ProviderConfig,
  ): Promise<ProviderRoute> {
    const base = (cfg.osrmEndpoint || 'https://routing.openstreetmap.de').replace(/\/$/, '');
    const path = cfg.osrmEndpoint ? profile : PROFILE_PATH[profile];
    const url = `${base}/${path}/route/v1/${OSRM_MODE[profile]}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=polyline&alternatives=false&steps=false`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), cfg.timeoutMs ?? 8000);
    try {
      const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        code?: string;
        routes?: Array<{ distance: number; duration?: number; geometry: string }>;
      };
      if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
        throw new Error(data.code ?? 'NoRoute');
      }
      const r = data.routes[0];
      const geometry = decodePolyline(r.geometry);
      if (geometry.length < 2) throw new Error('EmptyGeometry');
      return {
        geometry,
        distanceKm: r.distance / 1000,
        durationMin: Number.isFinite(r.duration) ? (r.duration as number) / 60 : null,
      };
    } finally {
      clearTimeout(timer);
    }
  },
};
